import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { sendChatbotMessage } from "../../services/post";
import { useConfig } from "../ConfigContext";
import { useClientAccessContext } from "../ClientAccessContext";

export enum ChatPhase {
	ASKING = "asking",
	LOADING = "loading",
}

export enum ChatMessageAuthor {
	BOT = "bot",
	USER = "user",
	SYSTEM = "system",
}

export interface ChatMessage {
	author: ChatMessageAuthor;
	text: string;
	timestamp: number;
	isMarkdown?: boolean;
	isError?: boolean;
}

export interface ChatSelectedDocument {
	id: number;
	title: string;
}

interface IChatProvider {
	isChatOpen: boolean;
	phase: ChatPhase;
	isBotTyping: boolean;
	messages: ChatMessage[];
	selectedDocuments: ChatSelectedDocument[];
	isSelectionActive: boolean;
	toggleChat: () => void;
	closeChat: () => void;
	isDocumentSelected: (documentId: number) => boolean;
	toggleDocumentSelection: (document: ChatSelectedDocument) => void;
	removeDocumentFromSelection: (documentId: number) => void;
	sendQuestion: (question: string) => void;
	resetConversation: () => void;
}

interface ChatContextProps {
	children?: React.ReactNode;
}

const WELCOME_MESSAGE =
	"Hi! Ask me anything. To ask about specific documents, select them with the ✓ on the cards.";

const ERROR_MESSAGE = "Something went wrong. Please try again.";

const describeScope = (documentCount: number) =>
	documentCount
		? `Asking about ${documentCount} document${documentCount > 1 ? "s" : ""}`
		: "Asking without document context";

const isSameSelection = (ids: number[], previousIds: number[] | null) =>
	previousIds !== null &&
	ids.length === previousIds.length &&
	ids.every((id) => previousIds.includes(id));

const BOT_TYPING_DELAY_MS = 500;

const ChatContext = createContext({} as IChatProvider);

const ChatProvider: React.FunctionComponent<ChatContextProps> = ({
	children,
}) => {
	const { selectedTopic } = useConfig();
	const { selectedClient } = useClientAccessContext();
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [phase, setPhase] = useState<ChatPhase>(ChatPhase.ASKING);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [selectedDocuments, setSelectedDocuments] = useState<
		ChatSelectedDocument[]
	>([]);
	const [isBotTyping, setIsBotTyping] = useState(false);
	const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const sessionIdRef = useRef(crypto.randomUUID());
	const previousClientIdRef = useRef(selectedClient?.clientId);
	const lastSentDocumentIdsRef = useRef<number[] | null>(null);
	//comment

	const addMessage = (message: Omit<ChatMessage, "timestamp">) => {
		setMessages((prev) => [...prev, { ...message, timestamp: Date.now() }]);
	};

	const addBotMessageDelayed = (
		message: Omit<ChatMessage, "timestamp" | "author">,
		onShown?: () => void,
	) => {
		if (typingTimeoutRef.current) return;
		setIsBotTyping(true);
		typingTimeoutRef.current = setTimeout(() => {
			typingTimeoutRef.current = null;
			setIsBotTyping(false);
			addMessage({ ...message, author: ChatMessageAuthor.BOT });
			onShown?.();
		}, BOT_TYPING_DELAY_MS);
	};

	const toggleChat = () => {
		if (!isChatOpen && !messages.length) {
			addBotMessageDelayed({ text: WELCOME_MESSAGE });
		}
		setIsChatOpen((prev) => !prev);
	};

	const closeChat = () => setIsChatOpen(false);

	const isDocumentSelected = (documentId: number) =>
		selectedDocuments.some((d) => d.id === documentId);

	const toggleDocumentSelection = (document: ChatSelectedDocument) => {
		setSelectedDocuments((prev) => {
			if (prev.some((d) => d.id === document.id)) {
				return prev.filter((d) => d.id !== document.id);
			}
			return [...prev, document];
		});
	};

	const removeDocumentFromSelection = (documentId: number) => {
		setSelectedDocuments((prev) => prev.filter((d) => d.id !== documentId));
	};

	const sendQuestion = async (question: string) => {
		const trimmedQuestion = question.trim();
		if (!trimmedQuestion || phase !== ChatPhase.ASKING) return;
		const documentIds = selectedDocuments.map((d) => d.id);
		if (!isSameSelection(documentIds, lastSentDocumentIdsRef.current)) {
			if (documentIds.length || lastSentDocumentIdsRef.current?.length) {
				addMessage({
					author: ChatMessageAuthor.SYSTEM,
					text: describeScope(documentIds.length),
				});
			}
			lastSentDocumentIdsRef.current = documentIds;
		}
		addMessage({ author: ChatMessageAuthor.USER, text: trimmedQuestion });
		setPhase(ChatPhase.LOADING);
		const sessionId = sessionIdRef.current;
		const response = await sendChatbotMessage({
			message: trimmedQuestion,
			documentIds,
			sessionId,
		});
		if (sessionId !== sessionIdRef.current) return;
		if (!response?.isSuccess) {
			addMessage({
				author: ChatMessageAuthor.BOT,
				text: response?.errorMessages?.[0] || ERROR_MESSAGE,
				isError: true,
			});
			setPhase(ChatPhase.ASKING);
			return;
		}
		addMessage({
			author: ChatMessageAuthor.BOT,
			text: response.result.message,
			isMarkdown: true,
		});
		setPhase(ChatPhase.ASKING);
	};

	const resetConversation = (reopenWelcome = isChatOpen) => {
		sessionIdRef.current = crypto.randomUUID();
		lastSentDocumentIdsRef.current = null;
		if (typingTimeoutRef.current) {
			clearTimeout(typingTimeoutRef.current);
			typingTimeoutRef.current = null;
		}
		setIsBotTyping(false);
		setMessages([]);
		setSelectedDocuments([]);
		setPhase(ChatPhase.ASKING);
		if (reopenWelcome) {
			addBotMessageDelayed({ text: WELCOME_MESSAGE });
		}
	};

	useEffect(() => {
		const clientChanged =
			previousClientIdRef.current !== selectedClient?.clientId;
		previousClientIdRef.current = selectedClient?.clientId;
		if (clientChanged) {
			setIsChatOpen(false);
		}
		resetConversation(!clientChanged && isChatOpen);
	}, [selectedClient?.clientId, selectedTopic?.id]);

	const chatContextValue: IChatProvider = {
		isChatOpen,
		phase,
		isBotTyping,
		messages,
		selectedDocuments,
		isSelectionActive: isChatOpen,
		toggleChat,
		closeChat,
		isDocumentSelected,
		toggleDocumentSelection,
		removeDocumentFromSelection,
		sendQuestion,
		resetConversation,
	};

	return (
		<ChatContext.Provider value={chatContextValue}>
			{children}
		</ChatContext.Provider>
	);
};

const useChat = () => {
	const context = useContext(ChatContext);
	if (context === undefined) {
		throw new Error("useChat must be used within a ChatProvider");
	}
	return context;
};

export { ChatProvider, useChat };
